import { getCollectionData, getCollectionDataById } from "../../helpers/index";
import runTransaction from "../../utils/runTransaction";

import articleDeliveryStatuses from "../articles/constants/articleDeliveryStatuses";
import updateArticle from "../articles/updateArticle";

import clearScheduledDistributions from "./clearScheduledDistributions";
import getArticleAutoDeliverySettings from "./getArticleAutoDeliverySettings";
import getArticleDistributionPresets from "./getArticleDistributionPresets";
import getTriggerDistributionQueueData from "./getTriggerDistributionQueueData";
import queueOrScheduleDistributions from "./queueOrScheduleDistributions";
import { canDeliverArticleToChannel } from "./utils/deliverArticlesToChannels";
import validateDistribution from "./validateDistribution";

export default async function distributeArticles(
	firebase,
	{ articles, delivery, channels, onlyDeliveryNeeded },
	{ transaction } = {},
) {
	if (!articles || !articles.length) {
		throw new Error("No articles provided");
	}

	const publisherIds = [...new Set(articles.map((article) => article.publisher?.id))];

	if (publisherIds.length > 1) {
		throw new Error("All articles must belong to the same publisher");
	}

	const distributionChannelsRef = firebase
		.firestore()
		.collection("distributionChannels")
		.where("deleted", "==", false);

	const channelsToDistributeTo = channels
		? channels
		: delivery
		? await getCollectionDataById(firebase, distributionChannelsRef, Object.keys(delivery))
		: await getCollectionData(distributionChannelsRef);

	return runTransaction(
		firebase,
		[articles[0].publisher, articles],
		async (transaction, [publisher, articles]) => {
			const results = await Promise.all(
				articles.map(async (article) => ({
					article,
					results: await distributeArticle(
						firebase,
						{
							article,
							delivery,
							publisher,
							channels: channelsToDistributeTo,
							onlyDeliveryNeeded,
						},
						{ transaction },
					),
				})),
			);

			// trigger check of distribution queue
			const triggerDistributionQueueData = getTriggerDistributionQueueData(firebase);

			transaction.update(triggerDistributionQueueData.ref, triggerDistributionQueueData);

			return results;
		},
		transaction,
	);
}

export async function distributeArticle(
	firebase,
	{ article, channels, delivery, publisher, onlyDeliveryNeeded },
	{ transaction } = {},
) {
	const deliveries = getAutoDeliveries({ article, publisher, channels, delivery, onlyDeliveryNeeded }).filter(
		({ delivery }) => Object.values(delivery).some(Boolean),
	);

	if (deliveries.length === 0) {
		return;
	}

	const channelIds = deliveries.map(({ channel }) => channel.id);

	// Validate and queue/schedule deliveries
	const results = await Promise.all(
		deliveries.map(async ({ channel, delivery }) => {
			try {
				await validateDistribution({ article, channel, delivery });

				const distributions = queueOrScheduleDistributions(firebase, {
					publisher,
					article,
					channel,
					delivery,
				});

				return { channel, delivery, distributions };
			} catch (error) {
				return { isbn: article.isbn, channel, delivery, error };
			}
		}),
	);

	// Update article delivery statuses based on results
	const articleData = results.reduce((data, { error, channel, delivery, distributions }) => {
		const deliveryTypes = Object.entries(delivery)
			.filter(([, ordered]) => ordered)
			.map(([deliveryType]) => deliveryType);

		const failedDeliveryTypes = error?.deliveryTypes
			? deliveryTypes.filter((deliveryType) => error.deliveryTypes.includes(deliveryType))
			: deliveryTypes;

		return {
			...data,
			...deliveryTypes
				.filter((deliveryType) => {
					if (error) {
						return failedDeliveryTypes.includes(deliveryType);
					}
					return distributions.some((d) => d.delivery[deliveryType]);
				})
				.reduce((acc, deliveryType) => {
					const distribution = distributions ? distributions.find((d) => d.delivery[deliveryType]) : null;

					return {
						...acc,
						[`channels.${channel.id}.delivery.${deliveryType}.status`]: error
							? articleDeliveryStatuses.DELIVERY_FAILED
							: articleDeliveryStatuses.DELIVERY_PENDING,
						[`channels.${channel.id}.delivery.${deliveryType}.error`]: error ? error.message : "",
						[`channels.${channel.id}.delivery.${deliveryType}.distribution`]: distribution,
					};
				}, {}),
		};
	}, {});

	await Promise.all([
		updateArticle(firebase, { ref: article.ref, ...articleData }, { transaction }),
		clearScheduledDistributions(
			firebase,
			{
				article,
				channelIds,
			},
			{ transaction },
		),
	]);

	const distributions = results.flatMap((result) => result.distributions || []);

	for (const distribution of distributions) {
		transaction.set(distribution.ref, distribution);
	}

	return results;
}

export function getAutoDeliveries({ article, publisher, channels, delivery, onlyDeliveryNeeded }) {
	return getArticleDistributionPresets({ article, publisher, channels })
		.presetChannels?.filter((channel) => canDeliverArticleToChannel({ article, channel }))
		.map((channel) => ({
			channel,
			delivery: getArticleAutoDeliverySettings({
				article,
				channel,
				overrides: delivery?.[channel.id],
				onlyDeliveryNeeded,
			}),
		}));
}
